import { KLAYO_COLORS } from '@/themes';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, Typography } from '@mui/material';
import { ConfirmationDialog } from 'klayowebshared';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { addEmployeeSchema, AddEmployeeForm } from '../../utilities/addEmployeeSchema';
import { TextField } from '../TextField';
import { Dialog } from './Dialog';

interface AddEmployeeDialogProps {
  translate: (key: string) => string;
  theme: any;
  onClose: () => void;
  onSave: (data: AddEmployeeForm) => Promise<void>;
  onChangeToFull: (data: AddEmployeeForm) => void;
}

const defaultValues: AddEmployeeForm = { firstName: '', lastName: '', email: '' };

export function AddEmployeeDialog({
  translate,
  theme,
  onClose,
  onSave,
  onChangeToFull,
}: AddEmployeeDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid, dirtyFields },
    reset,
  } = useForm<AddEmployeeForm>({
    defaultValues,
    resolver: zodResolver(addEmployeeSchema),
    mode: 'onChange',
  });

  const isFormDirty = !isEmpty(dirtyFields);

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const onShowWarnCloseDialog = () => {
    if (isFormDirty) return setShowConfirmClose(true);
    handleClose();
  };

  const onSubmit = async (data: AddEmployeeForm) => {
    setIsLoading(true);
    try {
      await onSave(data);
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open
        theme={theme}
        title={translate('settingsViews.employees.addEmployeeDialog.title')}
        topCloseButton={false}
        maxWidth='sm'
        fullWidth
        onClose={onShowWarnCloseDialog}
        actions={[
          {
            label: translate('settingsViews.employees.addEmployeeDialog.addEmployee'),
            variant: 'filled',
            primary: true,
            form: 'add-employee-form',
            disabled: !isValid || isLoading,
            isLoading,
          },
          {
            label: translate('common.cancel'),
            onClick: onShowWarnCloseDialog,
            disabled: isLoading,
          },
        ]}
      >
        <Stack spacing={3}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Stack spacing={0.5}>
              <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                {translate('settingsViews.employees.addEmployeeDialog.accessType')}
              </Typography>
              <Typography variant='body2' fontWeight={500} color={KLAYO_COLORS.GreyGR900}>
                {translate('settingsViews.employees.limitedAccessLabel')}
              </Typography>
            </Stack>
            <Typography
              variant='body2'
              fontWeight={500}
              color={KLAYO_COLORS.NeonBlueNB500}
              sx={{ cursor: 'pointer' }}
              onClick={() => onChangeToFull(getValues())}
            >
              {translate('settingsViews.employees.addEmployeeDialog.changeToFull')}
            </Typography>
          </Stack>

          <Stack
            component='form'
            id='add-employee-form'
            onSubmit={handleSubmit(onSubmit)}
            spacing={3}
          >
            <Controller
              control={control}
              name='firstName'
              render={({ field, fieldState }) => (
                <TextField
                  label={translate('labels.firstName')}
                  fullWidth
                  clearable={false}
                  autoComplete={false}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? translate(fieldState.error.message ?? '') : null}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name='lastName'
              render={({ field, fieldState }) => (
                <TextField
                  label={translate('labels.lastName')}
                  fullWidth
                  clearable={false}
                  autoComplete={false}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? translate(fieldState.error.message ?? '') : null}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name='email'
              render={({ field, fieldState }) => (
                <TextField
                  label={translate('labels.email')}
                  fullWidth
                  clearable={false}
                  autoComplete={false}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? translate(fieldState.error.message ?? '') : null}
                  {...field}
                />
              )}
            />
          </Stack>
        </Stack>
      </Dialog>

      {showConfirmClose && (
        <ConfirmationDialog
          theme={theme}
          title={translate('common.unsavedChangesTitle')}
          question={translate('common.unsavedChangesQuestion')}
          cancelButton={translate('common.cancel')}
          acceptButton={translate('common.leavePage')}
          onCancel={() => setShowConfirmClose(false)}
          onAccept={handleClose}
        />
      )}
    </>
  );
}
